import { useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import Button from 'react-bootstrap/Button';

import { useVideoProjectCreation } from '@contexts/VideoProjectCreation.context';
import { useFormContext } from 'react-hook-form';
import { formatTime, formatFileSize } from '@utils/video/helpers';
import { getModelLabel } from '@config/videoDetectionModelConfig';
import LoadingDots from '@components/loading/LoadingDots';

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
`;

const SummaryContainer = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 1.5rem;

	@media (max-width: 991px) {
		grid-template-columns: 1fr;
	}
`;

const Panel = styled.div`
	padding: 1.5rem;
	background: rgba(${({ theme }) => theme.colors.borderLightRgb}, 0.1);
	border-radius: 0.375rem;
	border: 1px solid rgba(${({ theme }) => theme.colors.borderLightRgb}, 0.2);
`;

const PanelTitle = styled.h4`
	margin: 0 0 1rem 0;
	font-size: 1.125rem;
	font-weight: 600;
`;

const InfoRow = styled.div`
	display: flex;
	justify-content: space-between;
	padding: 0.5rem 0;

	&:not(:last-child) {
		border-bottom: 1px solid rgba(255, 255, 255, 0.1);
	}
`;

const Label = styled.span`
	color: rgba(255, 255, 255, 0.7);
	font-size: 0.875rem;
`;

const Value = styled.span`
	color: #ffffff;
	font-weight: 500;
	font-size: 0.875rem;
`;

const UploadSection = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 1rem;
	padding: 2rem;
	background: rgba(${({ theme }) => theme.colors.borderLightRgb}, 0.1);
	border-radius: 0.375rem;
`;

const ProgressContainer = styled.div`
	width: 100%;
	max-width: 600px;
`;

const ProgressBar = styled.div`
	width: 100%;
	height: 30px;
	background: rgba(0, 0, 0, 0.3);
	border-radius: 15px;
	overflow: hidden;
	position: relative;
`;

const shimmer = keyframes`
	0% { background-position: -200% 0; }
	100% { background-position: 200% 0; }
`;

const ProgressFill = styled.div`
	height: 100%;
	background: linear-gradient(
		90deg,
		${({ theme }) => theme.colors.green} 0%,
		#00d26a 25%,
		${({ theme }) => theme.colors.green} 50%,
		#00d26a 75%,
		${({ theme }) => theme.colors.green} 100%
	);
	background-size: 200% 100%;
	animation: ${({ $complete }) => ($complete ? 'none' : css`${shimmer} 2s linear infinite`)};
	transition: width 0.3s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #ffffff;
	font-weight: 600;
	font-size: 0.875rem;
`;

const ProgressStats = styled.div`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 1rem;
	margin-top: 1rem;
	text-align: center;
`;

const StatItem = styled.div`
	label {
		display: block;
		color: rgba(255, 255, 255, 0.6);
		font-size: 0.75rem;
		margin-bottom: 0.25rem;
	}

	value {
		display: block;
		color: #ffffff;
		font-weight: 600;
		font-size: 1rem;
	}
`;

const VideoUpload = () => {
	const {
		videoFile,
		videoMetadata,
		configuration,
		uploadProgress,
		isUploading,
		createProjectAndUpload,
		previousStage,
	} = useVideoProjectCreation();
	const { watch } = useFormContext();

	const [isSubmitting, setIsSubmitting] = useState(false);

	const projectName = watch('projectName');
	const projectDescription = watch('projectDescription');

	const handleCreateProjectAndUpload = async () => {
		if (isSubmitting || isUploading) return;
		setIsSubmitting(true);
		try {
			await createProjectAndUpload();
		} finally {
			setIsSubmitting(false);
		}
	};

	const scenarioLabels = {
		fence_exit: 'Fence Exit',
		moving_drone: 'Moving Drone',
		stationary_drone: 'Stationary Drone',
	};

	const methodLabels = {
		unique_id: 'Unique Object',
		line_based: 'Virtual Line',
		// counting_zone: 'Counting Zone', // TODO: Enable when polygon drawing UI is ready
	};

	// Calculate upload stats (mock for now - would come from actual upload)
	const uploadedBytes = videoFile ? (videoFile.size * uploadProgress) / 100 : 0;
	const totalBytes = videoFile?.size || 0;

	return (
		<Container>
			<SummaryContainer>
				<Panel>
					<PanelTitle>Project Information</PanelTitle>
					<InfoRow>
						<Label>Project Name:</Label>
						<Value>{projectName}</Value>
					</InfoRow>
					{projectDescription && (
						<InfoRow>
							<Label>Description:</Label>
							<Value>{projectDescription}</Value>
						</InfoRow>
					)}
					<InfoRow>
						<Label>File Name:</Label>
						<Value>{videoFile?.name}</Value>
					</InfoRow>
					<InfoRow>
						<Label>Video Duration:</Label>
						<Value>{formatTime(videoMetadata?.duration || 0)}</Value>
					</InfoRow>
					<InfoRow>
						<Label>Resolution:</Label>
						<Value>
							{videoMetadata?.width} × {videoMetadata?.height}
						</Value>
					</InfoRow>
					<InfoRow>
						<Label>File Size:</Label>
						<Value>{formatFileSize(totalBytes)}</Value>
					</InfoRow>
				</Panel>

				<Panel>
					<PanelTitle>Configuration</PanelTitle>
					<InfoRow>
						<Label>Detection Model:</Label>
						<Value>{getModelLabel(configuration.modelOption?.value)}</Value>
					</InfoRow>
					<InfoRow>
						<Label>Counting Scenario:</Label>
						<Value>{scenarioLabels[configuration.countingScenario]}</Value>
					</InfoRow>
					<InfoRow>
						<Label>Counting Method:</Label>
						<Value>{methodLabels[configuration.countingMethod]}</Value>
					</InfoRow>
					{configuration.virtualLineDefinition && (
						<InfoRow>
							<Label>Virtual Line:</Label>
							<Value>Defined</Value>
						</InfoRow>
					)}
					<InfoRow>
						<Label>Time Range:</Label>
						<Value>
							{configuration.timeRange && configuration.timeRange.end_seconds > 0
								? `${formatTime(configuration.timeRange.start_seconds)} - ${formatTime(configuration.timeRange.end_seconds)}`
								: `0:00 - ${formatTime(videoMetadata?.duration || 0)}`}
						</Value>
					</InfoRow>
				</Panel>
			</SummaryContainer>

			<UploadSection>
				{isUploading || uploadProgress > 0 ? (
					<ProgressContainer>
						<ProgressBar>
							<ProgressFill $complete={uploadProgress === 100} style={{ width: `${uploadProgress}%` }}>
								{uploadProgress.toFixed(1)}%
							</ProgressFill>
						</ProgressBar>

						<ProgressStats>
							<StatItem>
								<label>Uploaded</label>
								<value>{formatFileSize(uploadedBytes)}</value>
							</StatItem>
							<StatItem>
								<label>Total</label>
								<value>{formatFileSize(totalBytes)}</value>
							</StatItem>
							<StatItem>
								<label>Status</label>
								<value>
									{uploadProgress === 100 ? 'Complete' : <>Uploading<LoadingDots /></>}
								</value>
							</StatItem>
						</ProgressStats>

						{/* Future: Add cancel button when implemented */}
						{/* <Button variant="danger" onClick={cancelUpload} disabled={uploadProgress === 100}>
							Cancel Upload
						</Button> */}
					</ProgressContainer>
				) : (
					<>
						<h3>Ready to Create Project?</h3>
						<p style={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center' }}>
							Click the button below to create your project and upload the video.
							<br />
							<span style={{ fontSize: '0.85rem' }}>
								Or go back to review or change your settings.
							</span>
						</p>
						<div
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								width: '100%',
							}}>
							<Button variant="secondary" onClick={previousStage}>
								← Back to Configuration
							</Button>
							<Button
								variant="success"
								onClick={handleCreateProjectAndUpload}
								disabled={isSubmitting || isUploading}>
								Create Project & Upload
							</Button>
						</div>
					</>
				)}
			</UploadSection>
		</Container>
	);
};

export default VideoUpload;
